import { CSS_VARIABLES_META } from '@/app/styles/CssVariables';
import clsx from 'clsx';
import type { ElementType } from 'react';
import React, { useEffect, useState } from 'react';
import type { FontWeight, TextDecoration, TextTransform, TextWrap, TTypographyProps } from './types';
import styles from './Typography.module.scss';

const fontWeightMap: Record<FontWeight, string> = {
	200: styles.fontWeight_200,
	300: styles.fontWeight_300,
	400: styles.fontWeight_400,
	500: styles.fontWeight_500,
	600: styles.fontWeight_600,
	700: styles.fontWeight_700,
};

const textDecorationMap: Record<TextDecoration, string> = {
	lineThrough: styles.textDecoration_lineThrough,
	none: styles.textDecoration_none,
	overline: styles.textDecoration_overline,
	underline: styles.textDecoration_underline,
};

const textWrapMap: Record<TextWrap, string> = {
	balance: styles.textWrap_balance,
	nowrap: styles.textWrap_nowrap,
	pretty: styles.textWrap_pretty,
	wrap: styles.textWrap_wrap,
};

const textTransformMap: Record<TextTransform, string> = {
	capitalize: styles.textTransform_capitalize,
	fullWidth: styles.textTransform_fullWidth,
	lowercase: styles.textTransform_lowercase,
	none: styles.textTransform_none,
	uppercase: styles.textTransform_uppercase,
};

const Typography = <T extends ElementType = 'span'>({
	as,
	variant,
	variantS,
	className,
	color,
	fontWeight,
	textTransform = 'none',
	htmlProps,
	textDecoration = 'none',
	textWrap = 'wrap',
	children,
	style,
	...rest
}: TTypographyProps<T>) => {
	const Tag = as || 'p';
	const [clientWidth, setClientWidth] = useState<number>(window.innerWidth);

	useEffect(() => {
		const handleResize = () => {
			setClientWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const getClasses = clsx(
		styles.typography,
		variantS ? (clientWidth <= 381 ? styles[variantS] : styles[variant]) : styles[variant],
		className,
		{
			// eslint-disable-next-line max-len
			[fontWeightMap[fontWeight ?? 200]]: fontWeight && Object.prototype.hasOwnProperty.call(fontWeightMap, fontWeight),

			[textTransformMap[textTransform]]:
				Object.prototype.hasOwnProperty.call(textTransformMap, textTransform) && textTransform !== 'none',

			[textDecorationMap[textDecoration]]:
				Object.prototype.hasOwnProperty.call(textDecorationMap, textDecoration) && textDecoration !== 'none',

			[textWrapMap[textWrap]]: Object.prototype.hasOwnProperty.call(textWrapMap, textWrap) && textWrap !== 'wrap',
		},
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	// const { variantS: _, ...cleanRest } = rest;

	return (
		<Tag
			className={getClasses}
			style={{
				...(color && { color: CSS_VARIABLES_META[color] }),
				...style,
			}}
			{...htmlProps}
			{...rest}
		>
			{children}
		</Tag>
	);
};

export default Typography;
